import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ConditionTree, ActionsSection, MetadataSection, JsonPreview, DryRunPanel } from './builder';
import { useRulesetSchema, useCreateRuleset, useUpdateRuleset, useRulesetValidation } from './hooks';
import { AppContext } from '../../App';
import {
  buildCopyLabel,
  createEmptyConditionGroup,
  DEFAULT_RULESET_PRIORITY,
  IConditionNode,
  IRulesetAction,
  IRuleset,
  normalizeRootConditionNode,
  pruneInapplicableActionParams,
  RulesetBuilderMode,
  serializeConditionNode,
  stripConditionIds,
} from '../../types/ruleset-types';

const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
const DEFAULT_ACTION_TYPE = 'set_training_set';
const getActionType = (action: { type?: string; name?: string }) => action.type || action.name || '';

interface RulesetBuilderDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  dataPoolId: string;
  mode: RulesetBuilderMode;
  /** Value source for Edit and Copy modes. */
  ruleset?: IRuleset | null;
  /** Labels on the loaded list page; only read in Copy mode to dedupe the copy label. */
  existingLabels?: string[];
}

const RulesetBuilderDrawer: React.FC<RulesetBuilderDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  dataPoolId,
  mode,
  ruleset,
  existingLabels = [],
}) => {
  const { setToast } = useContext(AppContext);
  // Edit and Copy need a source ruleset; without one the drawer is a plain create
  // so the header, submit label and mutation never disagree with the form.
  const isEditMode = mode === RulesetBuilderMode.Edit && !!ruleset;
  const isCopyMode = mode === RulesetBuilderMode.Copy && !!ruleset;

  // Form state
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(DEFAULT_RULESET_PRIORITY);
  const [isActive, setIsActive] = useState(true);
  const [conditions, setConditions] = useState<IConditionNode>(
    createEmptyConditionGroup(generateId(), 'and')
  );
  const [actions, setActions] = useState<IRulesetAction[]>([]);
  const [labelTouched, setLabelTouched] = useState(false);
  const [didInitializeDefaultAction, setDidInitializeDefaultAction] = useState(false);

  // Fetch schema
  const { data: schema, isLoading: isLoadingSchema, isError: isSchemaError } = useRulesetSchema(
    dataPoolId,
    open
  );

  // A param the action has retired (a `reason` left behind by switching status
  // away from EXCLUDED, or one loaded from a ruleset saved before the builder
  // knew better) must not reach the preview or the save payload.
  const effectiveActions = useMemo(() => pruneInapplicableActionParams(actions), [actions]);

  // Validation
  const { isValid, firstError } = useRulesetValidation({
    label,
    conditions,
    actions: effectiveActions,
    schemaFields: schema?.fields || [],
    schemaActions: schema?.actions || [],
  });

  // Mutations
  const { mutate: createRuleset, isPending: isCreating } = useCreateRuleset({
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Ruleset created successfully',
      });
      onSuccess();
    },
    onError: (error) => {
      setToast({
        variant: 'error',
        open: true,
        message: error?.message || 'Failed to create ruleset',
      });
    },
  });

  const { mutate: updateRuleset, isPending: isUpdating } = useUpdateRuleset({
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Ruleset updated successfully',
      });
      onSuccess();
    },
    onError: (error) => {
      setToast({
        variant: 'error',
        open: true,
        message: error?.message || 'Failed to update ruleset',
      });
    },
  });

  const isSaving = isCreating || isUpdating;

  // Initialize/reset form when drawer opens or ruleset changes
  useEffect(() => {
    if (open) {
      if (isCopyMode && ruleset) {
        setLabel(buildCopyLabel(ruleset.label, existingLabels));
        setDescription(ruleset.description || '');
        setPriority(ruleset.priority);
        setIsActive(ruleset.is_active);
        setConditions(
          normalizeRootConditionNode(stripConditionIds(ruleset.conditions), generateId)
        );
        setActions(ruleset.actions.map((action) => ({ ...action })));
        setDidInitializeDefaultAction(true);
      } else if (isEditMode && ruleset) {
        setLabel(ruleset.label);
        setDescription(ruleset.description || '');
        setPriority(ruleset.priority);
        setIsActive(ruleset.is_active);
        setConditions(normalizeRootConditionNode(ruleset.conditions, generateId));
        setActions(ruleset.actions);
        setDidInitializeDefaultAction(true);
      } else {
        setLabel('');
        setDescription('');
        setPriority(DEFAULT_RULESET_PRIORITY);
        setIsActive(true);
        setConditions(createEmptyConditionGroup(generateId(), 'and'));
        setActions([]);
        setDidInitializeDefaultAction(false);
      }
      setLabelTouched(false);
    }
    // existingLabels is a fresh array every render; the label is seeded once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, ruleset]);

  useEffect(() => {
    if (!open || ruleset || didInitializeDefaultAction || !schema?.actions?.length) {
      return;
    }

    const defaultAction = schema.actions.find(
      (action) => getActionType(action) === DEFAULT_ACTION_TYPE
    );

    if (defaultAction) {
      setActions([{ type: DEFAULT_ACTION_TYPE }]);
    }

    setDidInitializeDefaultAction(true);
  }, [open, ruleset, didInitializeDefaultAction, schema]);

  // Compute label error only when touched
  const labelError = labelTouched && label.trim().length === 0 ? 'Label is required' : '';

  const handleSubmit = () => {
    if (!isValid) {
      setToast({
        variant: 'error',
        open: true,
        message: firstError,
      });
      return;
    }

    const data = {
      label,
      description: description || undefined,
      priority,
      is_active: isActive,
      conditions: serializeConditionNode(conditions),
      actions: effectiveActions,
    };

    if (isEditMode && ruleset) {
      updateRuleset({
        dataPoolId,
        rulesetId: ruleset.id,
        data,
      });
    } else {
      createRuleset({
        dataPoolId,
        data,
      });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '66.67%',
          maxWidth: 1000,
          minWidth: 600,
        },
        'data-cy': 'drawer-ruleset-builder',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {isEditMode ? 'Edit Ruleset' : isCopyMode ? 'Copy Ruleset' : 'Create Ruleset'}
          </Typography>
          {isCopyMode && ruleset && (
            <Typography variant="caption" color="text.secondary" data-cy="text-ruleset-copy-source">
              {`Copied from ${ruleset.label}`}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" data-cy="btn-close-ruleset-drawer">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 3,
          backgroundColor: '#F5F6FA',
        }}
      >
        {isLoadingSchema ? (
          <LinearProgress />
        ) : isSchemaError ? (
          <Typography color="error">Failed to load ruleset schema</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Metadata Section */}
            <Paper sx={{ p: 2.5 }}>
              <MetadataSection
                label={label}
                description={description}
                priority={priority}
                isActive={isActive}
                onLabelChange={setLabel}
                onDescriptionChange={setDescription}
                onPriorityChange={setPriority}
                onActiveChange={setIsActive}
                onLabelBlur={() => setLabelTouched(true)}
                labelError={labelError}
                disabled={isSaving}
              />
            </Paper>

            {/* Conditions Section */}
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Conditions
                </Typography>
                <Tooltip title="Define when this rule should match" arrow placement="top">
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Box
                sx={{
                  backgroundColor: 'grey.50',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <ConditionTree
                  node={conditions}
                  schema={{ fields: schema?.fields || [] }}
                  depth={0}
                  onChange={setConditions}
                  dataPoolId={dataPoolId}
                  onRemove={() => {
                    setConditions(createEmptyConditionGroup(generateId(), 'and'));
                  }}
                  isRoot
                  disabled={isSaving}
                />
              </Box>
            </Paper>

            {/* Actions Section */}
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Actions
                </Typography>
                <Tooltip title="What happens when a document matches" arrow placement="top">
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <ActionsSection
                actions={effectiveActions}
                schemaActions={schema?.actions || []}
                onChange={setActions}
                disabled={isSaving}
                dataPoolId={dataPoolId}
              />
            </Paper>

            {/* JSON Preview */}
            <JsonPreview
              label={label}
              description={description}
              priority={priority}
              isActive={isActive}
              conditions={conditions}
              actions={effectiveActions}
            />

            {/* Dry Run */}
            {isEditMode && ruleset && (
              <DryRunPanel
                dataPoolId={dataPoolId}
                rulesetId={ruleset.id}
                disabled={isSaving}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#fff',
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSaving}
          data-cy="btn-cancel-ruleset"
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || isLoadingSchema || isSaving}
          data-cy={isEditMode ? 'btn-update-ruleset' : 'btn-submit-ruleset'}
          sx={{
            textTransform: 'none',
            backgroundColor: '#1D9AAA',
            color: '#fff',
            '&:hover': { backgroundColor: '#178a99' },
          }}
        >
          {isSaving ? 'Saving...' : isEditMode ? 'Update Ruleset' : 'Create Ruleset'}
        </Button>
      </Box>
    </Drawer>
  );
};

export default RulesetBuilderDrawer;
